import StateObserver, { now, user } from 'src/StateObserver';
import { setCompensation } from 'src/game/inventory/inventory.redux';
import { openProfile } from 'src/game/profile/profile.sequence';
import { track } from 'src/lib/analytics/utils';
import {
  openPopupPromise,
  PopupDialogOrigin,
} from 'src/lib/popups/popupOpenClose';
import { foregroundAsyncGetter, useCache, withLoading } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';
import { FixedGemsReward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { UnlcaimedCompetitionData } from 'src/replicant/team/competition/teamCompetition.ruleset';
import {
  getCurrentTeamEventId,
  getCurrentTeamEventSchedule,
} from 'src/replicant/team/team.getters';
import { TeamEventType } from 'src/replicant/team/team.ruleset';
import { assertNever } from 'src/replicant/utils';
import { duration } from 'src/replicant/utils/duration';

export async function openTeamsPopup(args: {
  selectedTab?: number;
  origin: PopupDialogOrigin | undefined;
}) {
  const { selectedTab, origin } = args;

  // Need to have a public profile to be in a team.
  if (!user().publicProfile) {
    await openProfile(origin);
  }

  await openPopupPromise('popupTeam', { selectedTab, origin });
}

/**
 * Open the current team event popup if there's an event running.
 */
export async function openTeamEventPopup(args: {
  eventType?: TeamEventType;
  origin: PopupDialogOrigin | undefined;
}) {
  let { eventType } = args;
  const { origin } = args;

  if (!eventType) {
    const currentEvent = getCurrentTeamEventSchedule(now());

    // Skip if there's no event running.
    if (!currentEvent) return;

    // Get event type, fallback to competition if undefined.
    eventType = currentEvent.type ?? 'competition';
  }

  // Run a popup sequence based on the event type.
  switch (eventType) {
    case 'competition':
      await openTeamCompetitionPopup(origin);
      break;
    case 'chest':
      await openTeamChestPopup(origin);
      break;
    default:
      assertNever(eventType);
  }
}

export async function openTeamCompetitionPopup(
  origin: PopupDialogOrigin | undefined,
) {
  // No teams, prompt to enter a team.
  if (!user().team.currentTeamId) {
    const result = await openPopupPromise('popupTeamCompetitionIntro', {
      origin,
    });
    if (!result) return;
    await openTeamsPopup({ selectedTab: 0, origin });
    return;
  }

  let matchMakingPromise: Promise<void> | undefined;

  // Claim rewards if necessary.
  const claimed = await claimCompetitionRewards(() => {
    // Start matchmaking when we show the popups, so that we can avoid showing the spinner.
    matchMakingPromise = runTeamCompetitionMatchmaking();
  }, origin);

  // Stop here if rewards were claimed, so user can tap to start a new event, that might not necessarily be the same type.
  if (claimed) return;

  if (!getCurrentTeamEventId(now())) return;

  // Show intro popup if necessary.
  if (user().team.lastCompetitionEventId !== getCurrentTeamEventId(now())) {
    const eventPromise = matchMakingPromise?.then(
      () => getCurrentTeamCompetition()?.get() ?? Promise.resolve(undefined),
    );

    await openPopupPromise('popupTeamCompetitionIntro', { origin });
    if (!user().team.seenCompetitionInfo) {
      await openPopupPromise('popupTeamCompetitionInfo', { origin });
    }

    await StateObserver.invoke.setLastCompetitionId();

    const event = await withLoading(
      () => eventPromise ?? Promise.resolve(undefined),
      {
        feature: Feature.TEAM._,
        subFeature: Feature.TEAM.CONPETITION_EVENT,
      },
    );
  }

  // Show spinner if matchmaking is still running.
  await withLoading(
    () => matchMakingPromise ?? runTeamCompetitionMatchmaking(),
    {
      feature: Feature.TEAM._,
      subFeature: Feature.TEAM.MATCHMAKING,
    },
  );

  // Show leaderboard.
  await openPopupPromise('popupTeamCompetitionLeaderboard', { origin });
}

/**
 * Open the team chest popup, showing the intro before, if user has not joined the event yet.
 * If user is not in a team, that will show the regular team popup instead.
 */
export async function openTeamChestPopup(
  origin: PopupDialogOrigin | undefined,
) {
  // Check if user is in a team, and open regular team popup if not.
  if (!user().team.currentTeamId) {
    const result = await openPopupPromise('popupTeamChestIntro', { origin });
    if (!result) return;
    // No teams, prompt to enter a team.
    await openTeamsPopup({ selectedTab: 0, origin });
    return;
  }

  // Don't do anything if there's no event running.
  if (!getCurrentTeamEventId(now())) return;

  // Claim rewards if necessary.
  let matchMakingPromise: Promise<void> | undefined;
  const claimed = await claimTeamChestRewards({
    startMatchMaking: () => {
      matchMakingPromise = runTeamCompetitionMatchmaking();
    },
    origin,
  });
  if (claimed) return;

  // Show intro popup if necessary.
  if (user().team.lastCompetitionEventId !== getCurrentTeamEventId(now())) {
    const eventPromise = matchMakingPromise?.then(
      () => getCurrentTeamCompetition()?.get() ?? Promise.resolve(undefined),
    );

    await openPopupPromise('popupTeamChestIntro', { origin });
    await StateObserver.invoke.setLastCompetitionId();
    const event = await withLoading(
      () => eventPromise ?? Promise.resolve(undefined),
      {
        feature: Feature.TEAM._,
        subFeature: Feature.TEAM.CHEST_EVENT,
      },
    );
    track('TeamChestJoin', {
      id: getCurrentTeamEventId(now()),
      teamId: user().team.currentTeamId,
      tournamentUniqueId: event?.sharedStateId,
    });
  }

  // Show spinner if matchmaking is still running.
  await withLoading(
    () => matchMakingPromise ?? runTeamCompetitionMatchmaking(),
    {
      feature: Feature.TEAM._,
      subFeature: Feature.TEAM.MATCHMAKING,
    },
  );

  // Show team chest leaderboard
  const claim = await openPopupPromise('popupTeamChestLeaderboard', { origin });

  // Claim rewards if necessary.
  if (claim) await claimTeamChestRewards({ origin });
}

export async function claimTeamChestRewards(args: {
  startMatchMaking?: () => void;
  origin: PopupDialogOrigin | undefined;
}) {
  const { startMatchMaking, origin } = args;

  // Check if there're rewards to claim.
  const unclaimedEvents =
    (await withLoading(async () => getUnclaimedTeamCompetition()?.get(), {
      feature: Feature.TEAM._,
      subFeature: Feature.TEAM.GET_UNCLAIMED_COMPETITION,
    })) ?? [];

  // No rewards to claim, bail.
  if (!unclaimedEvents.length) {
    // Call callback if provided.
    startMatchMaking?.();
    return false;
  }

  // Claim rewards in the backend.
  await withLoading(() => StateObserver.invoke.asyncClaimTeamEventRewards(), {
    feature: Feature.TEAM._,
    subFeature: Feature.TEAM.CLAIM_EVENT_REWARDS,
  });

  // At this point gems will be added to the user's account. Do not show it yet, until the popups are closed.
  const getTotalGemRewards = (event: UnlcaimedCompetitionData) =>
    event.rewards
      ?.filter((x) => x.type === 'gems')
      .reduce((a, b) => a + (b as FixedGemsReward).amount, 0) ?? 0;
  const gems = unclaimedEvents.reduce(
    (acc, curr) => acc + getTotalGemRewards(curr),
    0,
  );
  StateObserver.dispatch(setCompensation({ gems }));

  // Call callback if provided.
  startMatchMaking?.();

  // Invalidate cache.
  getUnclaimedTeamCompetition()?.invalidate();
  getCurrentTeamCompetition()?.invalidate();

  // Show popups.
  for (const event of unclaimedEvents) {
    // Show team chest leaderboard
    await openPopupPromise('popupTeamChestLeaderboard', {
      data: event,
      origin,
    });

    // Claim rewards if necessary.
    if (event.rewards) {
      await openPopupPromise('popupMatch2Chest', {
        rewards: event.rewards,
        chestType: 'team',
        origin,
      });
    }

    // Decrement the compensation.
    const rewardedGems = getTotalGemRewards(event);
    const currentCompensation =
      StateObserver.getState().inventory.compensation.gems;
    StateObserver.dispatch(
      setCompensation({
        gems: currentCompensation - rewardedGems,
      }),
    );

    // Send analytics for completed events.
    const team = event.teams.find((x) => x.current);

    track('TeamChestEnd', {
      id: event.id,
      teamId: team?.id,
      tournamentUniqueId: event?.sharedStateId,
      memberCount: event.myTeam.length,
      membersContributedCount: event.myTeam.filter((x) => x.score > 0).length,
      previousScore: event.previousTeamScore,
      totalGemsGranted: rewardedGems,
      totalCoffeeCollected: event.score,
    });
  }

  return true;
}

export async function claimCompetitionRewards(
  startMatchMaking: () => void,
  origin: PopupDialogOrigin | undefined,
) {
  // Check if there're rewards to claim.
  const unclaimedEvents =
    (await withLoading(async () => getUnclaimedTeamCompetition()?.get(), {
      feature: Feature.TEAM._,
      subFeature: Feature.TEAM.GET_UNCLAIMED_COMPETITION,
    })) ?? [];

  // No rewards to claim, bail.
  if (!unclaimedEvents.length) {
    startMatchMaking?.();
    return false;
  }

  // Claim rewards in the backend.
  await withLoading(() => StateObserver.invoke.asyncClaimTeamEventRewards(), {
    feature: Feature.TEAM._,
    subFeature: Feature.TEAM.CLAIM_EVENT_REWARDS,
  });

  // At this point gems will be added to the user's account. Do not show it yet, until the popups are closed.
  const getTotalGemRewards = (event: UnlcaimedCompetitionData) =>
    event.rewards
      ?.filter((x) => x.type === 'gems')
      .reduce((a, b) => a + (b as FixedGemsReward).amount, 0) ?? 0;
  const gems = unclaimedEvents.reduce(
    (acc, curr) => acc + getTotalGemRewards(curr),
    0,
  );
  StateObserver.dispatch(setCompensation({ gems }));

  // Invalidate cache.
  getUnclaimedTeamCompetition()?.invalidate();
  getCurrentTeamCompetition()?.invalidate();

  // Call callback if provided.
  startMatchMaking?.();

  // Show popups.
  for (const event of unclaimedEvents) {
    await openPopupPromise('popupTeamCompetitionLeaderboard', {
      data: event,
      origin,
    });

    // Decrement the compensation.
    const currentCompensation =
      StateObserver.getState().inventory.compensation.gems;
    StateObserver.dispatch(
      setCompensation({
        gems: currentCompensation - getTotalGemRewards(event),
      }),
    );
  }

  return true;
}

export async function runTeamCompetitionMatchmaking() {
  // No event running, bail.
  if (!getCurrentTeamEventId(now())) return;

  // Invalidate team profile cache if cached data does not match the current event.
  if (
    getCurrentTeamCompetition()?.getCached()?.id !==
    getCurrentTeamEventId(now())
  ) {
    await getCurrentTeamCompetition()?.invalidate();
  }

  const competition = await getCurrentTeamCompetition()?.get();

  // No competition, bail.
  if (!competition) return;
  // Cannot join current event, bail.
  if (!competition?.canJoin) return;
  // Already in an event, bail.
  if (competition?.joined) return;

  // Run matchmaking.
  await StateObserver.invoke.asyncJoinTeamEvent({});

  // Invalidate cache after matchmaking.
  await getCurrentTeamCompetition()?.invalidate();
}

const cacheLifetime = duration({ seconds: 20 });

let alreadyLoaded = false;
export function getCurrentTeamProfile() {
  const teamId = user().team.currentTeamId;
  if (!teamId) return undefined;

  const cache = useCache(
    foregroundAsyncGetter(() =>
      StateObserver.replicant.asyncGetters.getTeamProfile({}),
    ),
    `team_profile_${teamId}`,
    cacheLifetime,
  );

  return cache;
}

export function getCurrentTeamCompetition() {
  const teamId = user().team.currentTeamId;
  if (!teamId) return undefined;

  return useCache(
    () => StateObserver.replicant.asyncGetters.getCurrentTeamEvent(),
    `team_current_competition_${teamId}`,
    cacheLifetime,
  );
}

export function getUnclaimedTeamCompetition() {
  const teamId = user().team.currentTeamId;
  if (!teamId) return undefined;

  return useCache(
    foregroundAsyncGetter(() =>
      StateObserver.replicant.asyncGetters.getUnclaimedTeamEvents(),
    ),
    `team_unclaimed_competition_${teamId}`,
    cacheLifetime,
  );
}

export function getTeamMembers(teamId = user().team.currentTeamId) {
  if (!teamId) return undefined;

  const cache = useCache(
    () => StateObserver.replicant.asyncGetters.getTeamMembers({ teamId }),
    `team_members_${teamId}`,
    cacheLifetime,
  );

  return cache;
}

export async function getTeamMemberCount(
  fetch = false,
  teamId = user().team.currentTeamId,
) {
  const cacheAPI = getTeamMembers(teamId);
  if (!cacheAPI) return 0;
  const getter = fetch ? cacheAPI.get : cacheAPI.getCached;
  const members = await (getter() ?? cacheAPI.get());
  return members.length;
}

export async function trackTeamInteraction(
  type: 'chat' | 'emoji' | 'askEnergy' | 'sendEnergy',
) {
  const id = user().team.currentTeamId;
  if (!id) return;
  track('TeamInteraction', {
    id,
    memberCount: await getTeamMemberCount(),
    interactionType: type,
  });
}

export function getTeamChatMessages(args?: {
  lookingAtChat?: boolean;
  lastSeenAt?: number;
}) {
  const teamId = user().team.currentTeamId;
  const lastSeenAt = args?.lastSeenAt ?? user().team.lastSeenMessageTimestamp;
  const lookingAtChat = args?.lookingAtChat ?? false;

  if (!teamId) return undefined;

  return useCache(
    async () => {
      const messages =
        await StateObserver.replicant.asyncGetters.getTeamMessages({
          lastSeenAt,
        });
      const unreadMessages = messages.filter(
        (x) => !x.seen && x.senderId !== user().id,
      );
      const unreadRequest = unreadMessages.filter(
        (x) =>
          x.contents.type === 'energyRequest' ||
          x.contents.type === 'helpRequest',
      );

      return {
        messages,
        unreadMessagesCount: unreadMessages.length,
        unreadRequestCount: unreadRequest.length,
      };
    },
    `team_chat_messages_${teamId}`,
    lookingAtChat ? duration({ seconds: 5 }) : duration({ seconds: 30 }),
  );
}

/**
 * Team sequence to run after a puzzle is completed.
 * It will display any pending event rewards to be claimed, if any.
 * @returns
 */
export async function teamPostGameSequence() {
  // Check if there are unclaimed rewards, from the cached value - the team home button triggers the cached request.
  const unclaimed = getUnclaimedTeamCompetition()?.getCached();
  // Skip if there are no unclaimed rewards.
  if (!unclaimed || !unclaimed.length) return;
  // Show all unclaimed events in sequence
  for (const event of unclaimed) {
    await openTeamEventPopup({ eventType: event.type, origin: 'postLevel' });
  }
}
